import React from 'react';

import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { numberWithCommas } from '@clearverify/utils';

import {
  DashboardPlatformAdminTopVerifierDataInfoFragment,
  useGenerateRankedVerifiersReportMutation,
  VerifierType,
} from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardCVAdminData, useDashboardPrintView } from 'src/hooks/dashboard';
import { useDemoPrivacyMode } from 'src/hooks/demoPrivacyMode';
import { downloadFileByDownloadUrl } from 'src/utils/file';

import { DashboardChartWrapper } from '../DashboardChartWrapper';
import { DashboardVerifierTypeSelect } from '../DashboardVerifierTypeSelect/DashboardVerifierTypeSelect';

import { prepareDataset } from './utils';

export type DashboardTopVerifiersDataBlockProps = {
  topVerifiersData: DashboardPlatformAdminTopVerifierDataInfoFragment[];
  loading?: boolean;
};

export const DashboardTopVerifiersDataBlock: React.FC<DashboardTopVerifiersDataBlockProps> = ({
  topVerifiersData,
  loading,
}) => {
  const theme = useTheme();

  const { isPrintView } = useDashboardPrintView();
  const { verifierType, setVerifierType } = useDashboardCVAdminData();
  const { isDemoPrivacyModeEnabled } = useDemoPrivacyMode();

  const [generateRankedVerifiersReport, { loading: reportIsGenerating }] =
    useGenerateRankedVerifiersReportMutation();

  const getOptions = React.useCallback(
    (verifierType: VerifierType): ChartOptions => {
      const datasetByVerifierType = prepareDataset(
        topVerifiersData.filter(item => item.type === verifierType),
      );
      return {
        grid: { top: 30, right: 40, bottom: 55, left: 408 },
        animationDuration: isPrintView ? 0 : undefined,
        xAxis: {
          position: 'top',
          minInterval: 5,
          axisLabel: {
            fontSize: theme.typography.body1.fontSize,
            fontWeight: 'bold',
          },
          splitLine: {
            lineStyle: {
              type: [10, 10],
            },
          },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          offset: 400,
          axisLabel: {
            fontSize: theme.typography.body1.fontSize,
            fontFamily: theme.typography.body1.fontFamily,
            width: 400,
            align: 'left',
            overflow: 'truncate',
            color: theme.palette.text.primary,
          },
        },
        tooltip: {
          trigger: 'axis',
          padding: [4, 8, 4, 8],
          backgroundColor: theme.palette.slate?.[500],
          borderColor: theme.palette.slate?.[500],
          textStyle: {
            color: theme.palette.secondary.main,
          },
          // biome-ignore lint/suspicious/noExplicitAny: types doesn't match real behavior
          formatter: (params: any) => numberWithCommas(params[0].value.count),
        },
        series: [
          {
            type: 'bar',
            encode: {
              x: 'count',
              y: 'purposeValue',
            },
            barMaxWidth: 30,
            itemStyle: {
              borderRadius: 5,
            },
            label: isPrintView ? LABEL_OPTIONS : {},
            color: loading ? DashboardPalette.slate50 : DashboardPalette.green400,
          },
        ],
        dataset: [
          {
            source: datasetByVerifierType,
          },
        ],
      };
    },
    [topVerifiersData, isPrintView, loading, theme],
  );

  const baseOptions = React.useMemo(() => getOptions(verifierType), [getOptions, verifierType]);

  const handleExportClick = React.useCallback(async () => {
    const { data } = await generateRankedVerifiersReport({
      variables: {
        verifierType: verifierType,
      },
    });
    if (data?.generateRankedVerifiersReport) {
      downloadFileByDownloadUrl(
        data.generateRankedVerifiersReport.downloadUrl,
        data.generateRankedVerifiersReport.filename,
      );
    }
  }, [generateRankedVerifiersReport, verifierType]);

  const renderChart = (option: ChartOptions, height: number) => (
    <Box position="relative">
      <ChartBase option={option} style={{ height }} />
      {isDemoPrivacyModeEnabled ? (
        <Box
          sx={{
            position: 'absolute',
            top: 30,
            left: 0,
            width: 408,
            bottom: 55,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </Box>
  );

  return (
    <Paper sx={{ mt: 2, py: 2.5, px: 3, borderRadius: 2, overflow: 'auto' }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">Verification Requests per Verifier</Typography>
          <Typography variant="body1" color="text.secondary">
            The chart illustrates the top 10 verifiers based on the number of verification requests.
          </Typography>
        </Box>
        <Box height="4px">{loading ? <LinearProgress color="secondary" /> : <Divider />}</Box>
        <Box>
          {!isPrintView && (
            <Box display="flex" alignItems="center" gap={2}>
              <DashboardVerifierTypeSelect
                verifierType={verifierType}
                setVerifierType={setVerifierType}
              />
              <Box>
                <Button
                  variant="outlined"
                  size="small"
                  loading={reportIsGenerating}
                  onClick={handleExportClick}
                >
                  Export
                </Button>
              </Box>
            </Box>
          )}
          <DashboardChartWrapper isPrintView={isPrintView} topMargin={1}>
            {isPrintView ? (
              <React.Fragment>
                <Typography variant="body1" fontWeight="bold">
                  {VerifierType.Commercial}
                </Typography>
                {renderChart(getOptions(VerifierType.Commercial), 555)}
                <Typography variant="body1" fontWeight="bold">
                  {VerifierType.Government}
                </Typography>
                {renderChart(getOptions(VerifierType.Government), 555)}
              </React.Fragment>
            ) : (
              renderChart(baseOptions, 555)
            )}
          </DashboardChartWrapper>
        </Box>
      </Stack>
    </Paper>
  );
};

const LABEL_OPTIONS = {
  show: true,
  fontSize: '16px',
  fontWeight: 'bold' as const,
  formatter: '{@[1]}',
  position: 'right' as const,
  inside: false,
};
